import {
  COLOR_PROFILE_DEFAULT,
  COLOR_PROFILE_FIELD,
} from 'components/ImageViewer/constants';

export { COLOR_PROFILE_FIELD };

export const COLOR_PROFILES = {
  ORIGINAL: COLOR_PROFILE_DEFAULT,
  GT450: 'gt450',
  HE_CONTRAST: 'he_contrast',
};

// Pramana ('pramana', formerly shown as C) was removed: its ICC was the stock sRGB
// profile, so its LUT was an identity map. Re-add it once a real Pramana
// scanner calibration exists.
export const COLOR_PROFILE_OPTIONS = [
  { value: COLOR_PROFILES.ORIGINAL, label: 'Default' },
  { value: COLOR_PROFILES.GT450, label: 'Option A' },
  { value: COLOR_PROFILES.HE_CONTRAST, label: 'Option B' },
];

const KNOWN_COLOR_PROFILES = new Set(
  COLOR_PROFILE_OPTIONS.map(({ value }) => value),
);

// A saved preference can still name a removed profile, so anything the menu
// no longer offers falls back to Default (the untouched scanner output).
export const resolveColorProfile = (profileId) =>
  KNOWN_COLOR_PROFILES.has(profileId) ? profileId : COLOR_PROFILES.ORIGINAL;

// Each entry is a pre-baked RGB -> RGB 3D LUT served from the CRA public/
// folder so it is fetched lazily, only when a non-Default profile is
// selected. Option A is a scanner ICC baked by generate_luts.py; Option B is
// an H&E stain-deconvolution contrast boost from generate_he_contrast_lut.py.
const COLOR_PROFILE_ASSETS = {
  [COLOR_PROFILES.GT450]: `${process.env.PUBLIC_URL}/color-profiles/leica_gt450.lut65.json`,
  [COLOR_PROFILES.HE_CONTRAST]: `${process.env.PUBLIC_URL}/color-profiles/he_contrast.lut33.json`,
};

const lutCache = new Map();

const base64ToUint8Array = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

/**
 * Load (and cache) a color profile's 3D LUT.
 *
 * @param {string} profileId one of COLOR_PROFILES.*
 * @returns {Promise<{grid: number, data: Uint8Array}>} interleaved R,G,B bytes,
 *   indexed by ((B * grid + G) * grid + R) * 3.
 */
export async function getColorProfileLut(profileId) {
  const cached = lutCache.get(profileId);
  if (cached) {
    return cached;
  }

  const url = COLOR_PROFILE_ASSETS[profileId];
  if (!url) {
    throw new Error(`Unknown color profile: ${profileId}`);
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Failed to load color profile ${profileId} (${response.status})`,
    );
  }

  const meta = await response.json();
  const lut = { grid: meta.grid, data: base64ToUint8Array(meta.bytes) };
  lutCache.set(profileId, lut);

  return lut;
}
